/** @format */

import React, {useContext, useState} from 'react';
import SuperAgent from 'superagent';

import {
    Alert,
    Button,
    Icon,
    IconButton,
    Popover,
    QuoterValidation,
    Modal,
    ModalHeader,
    ModalFooter
} from '@sttark/design-system';

import {formatMoney, formatShortDate, prettyProductLabel} from '../functions/format';
import {
    REGULATORY_INDUSTRY_NOTAPPLICABLE,
    REGULATORY_INDUSTRY_TYPES,
    DESIGN_QUOTER_STEP_DESIGN_DIRECTION_BRAND_AND_PRODUCT,
    DESIGN_QUOTER_STEP_DESIGN_DIRECTION_COLOR_PREFERENCES,
    DESIGN_QUOTER_STEP_DESIGN_DIRECTION_DESIGN_STYLE,
    DESIGN_QUOTER_STEP_QUOTER,
    GRAPHIC_DESIGN_ORDER_TYPE_CUSTOM_DESIGN,
    ORDER_STATUS_IN_CART,
    ORDER_STATUS_QUOTE_SAVED
} from 'frontier-constants';

import '../../../account/namespaced_bs4.sass';
import QuoteLoginModal from '../quote_login_modal';
import ModalPortal, {ModalBody, ModalClose} from 'frontier-admin/modal/container_portal';

import {QuoteContext} from 'frontier-quoter';
import Section from '../section';

import '../order_summary.sass';
import {DesignDeliveryEstimator} from 'frontier/ds/quoter/design_delivery_estimator';
import useRequest from 'frontier/ds/quoter/hooks/use_request';
import {addCommas} from 'frontier/utilities/add_comas_to_number';
import {
    MODAL_DELETE_ORDER,
    MODAL_REMOVE_FROM_CART
} from '../../../cart/components/cart_overview/modal_constants';

export default function OrderSummary({isCustomFee}) {
    const ctx = useContext(QuoteContext);
    const {order, designOrder} = ctx;
    const {isComplete, errors} = ctx.handleIsComplete(order);

    // disable "Add to Cart" button immediately to avoid customers adding unwanted oders
    const [disableAddToCart, setDisableAddToCart] = useState(false);

    const isCustomArtwork = designOrder.design_type_id === GRAPHIC_DESIGN_ORDER_TYPE_CUSTOM_DESIGN;

    const [displayLoginModal, setDisplayLoginModal] = useState(false);
    const [showCreateAccount, setShowCreateAccount] = useState(true);

    const [showModal, setShowModal] = useState(0);

    const toggleDisplayLoginModal = () => {
        setDisplayLoginModal(!displayLoginModal);
    };

    const saveQuoteAndToggleDisplayLoginModal = () => {
        handleSaveQuote(order.id);
        setDisplayLoginModal(!displayLoginModal);
    };

    const {
        error: saveQuoteError,
        isPending: isSavingToAccount,
        request: handleSaveQuote
    } = useRequest(
        null,
        async () => {
            const date = formatShortDate(new Date());
            const time = new Date().toLocaleTimeString();
            const label = `Saved Quote - ${formatShortDate(date)} ${time}`;
            const {
                body: {order}
            } = await SuperAgent.post(`/carts/save-to-account/${ctx.order.id}`)
                .set('accept', 'application/json')
                .send({customer_label: label});

            ctx.handleSaveToAccount(order);
            setDisableAddToCart(false);
        },
        {initial: {}}
    );

    const handleLogin = response => {
        const {isLoggedIn} = response;

        // save order only after user logs in
        isLoggedIn && handleSaveQuote();

        // If they're logged in, pass it to the context so we can show the banner and allow further saved quotes.
        ctx.handleLogin(true);
        toggleDisplayLoginModal();
    };

    const handleShowCreateAccount = showCreateAccount => {
        setShowCreateAccount(showCreateAccount);
    };

    const steps = [
        DESIGN_QUOTER_STEP_DESIGN_DIRECTION_DESIGN_STYLE,
        DESIGN_QUOTER_STEP_DESIGN_DIRECTION_BRAND_AND_PRODUCT,
        DESIGN_QUOTER_STEP_DESIGN_DIRECTION_COLOR_PREFERENCES
    ];

    const stepIndex = steps.indexOf(ctx.designQuoterStep);

    // is it new order ora an order edit?
    const isOrderEdit = location.search.includes('edit');

    const canContinueToSecondStep =
        errors.findIndex(
            e =>
                e.category === 'size' ||
                e.category === 'form_factor' ||
                e.category === 'design_style_vector' ||
                e.category === 'substrate' ||
                e.category === 'industry'
        ) < 0;

    const canContinueToThirdStep = errors.findIndex(e => e.category === 'design_style') < 0;

    const canContinueToFourthStep =
        errors.findIndex(e => e.category === 'brand' || e.category === 'product') < 0;

    const canContinueToCheckout = errors.findIndex(e => e.category === 'color') < 0;

    return (
        <>
            <Modal
                onClose={() => setShowModal(false)}
                isOpen={showModal === 1}
                zIndex={1050}
                bodyHeightAuto>
                <ModalHeader onClose={() => setShowModal(false)}>Save Quote</ModalHeader>
                <div>
                    <p className="mb-2 mt-0 mx-0">
                        <b>You are about to save your current quote</b>
                    </p>
                    <p className="mt-0">
                        Saving this quote will move it to Saved Quotes on your account and you will
                        start from a brand new quote. Do you want to continue?{' '}
                    </p>
                </div>
                <ModalFooter>
                    <div className="d-flex align-items-center justify-content-end gap-4">
                        <Button onClick={() => setShowModal(0)} label="Cancel" secondary />
                        <Button
                            onClick={() => {
                                // if they are logged in save the quote
                                if (
                                    order.status_id !== ORDER_STATUS_QUOTE_SAVED &&
                                    ctx.isLoggedIn
                                ) {
                                    handleSaveQuote();
                                    setShowModal(0);
                                } else if (
                                    // otherwise, make them log in first before saving
                                    order.status_id !== ORDER_STATUS_QUOTE_SAVED &&
                                    !ctx.isLoggedIn
                                ) {
                                    setShowModal(0);
                                    toggleDisplayLoginModal();
                                } else {
                                    setShowModal(0);
                                    saveQuoteAndToggleDisplayLoginModal();
                                }
                            }}
                            label={'Save & Start Over'}
                        />
                    </div>
                </ModalFooter>
            </Modal>
            <Section enabled={!ctx.isAddingToCart && !ctx.isSaving}>
                <div className="my-4">
                    <div className="hstack justify-content-between font-body-bold-xl">
                        <span style={{display: 'flex', alignItems: 'center'}}>
                            Subtotal
                            {(ctx.isSaving || ctx.isAddingToCart) && (
                                <span className="saving font-body-sm pt-2 ps-2">
                                    <i className="fa fa-spinner fa-spin"></i>
                                </span>
                            )}
                        </span>
                        <span className="">{addCommas(formatMoney(order.quoted_total))}</span>
                    </div>
                </div>
            </Section>
            {stepIndex === -1 && isCustomArtwork && (
                <Button
                    fullWidth
                    className="mb-3"
                    label="Next: Design Direction"
                    onClick={() =>
                        ctx.handleSetDesignQuoterStep(
                            DESIGN_QUOTER_STEP_DESIGN_DIRECTION_DESIGN_STYLE
                        )
                    }
                    disabled={!canContinueToSecondStep || ctx.isAddingToCart || ctx.isSaving}
                />
            )}
            {stepIndex === -1 && !isCustomArtwork && (
                <Button
                    fullWidth
                    className="mb-3"
                    label={isOrderEdit ? 'I’m Finished, Back to Cart' : 'Add to Cart'}
                    onClick={() => {
                        setDisableAddToCart(true), ctx.handleAddToCart();
                    }}
                    disabled={!isComplete || ctx.isAddingToCart || ctx.isSaving}
                />
            )}

            {stepIndex !== -1 && (
                <>
                    <div>
                        {stepIndex === 0 && (
                            <div className="row">
                                <div className="col-sm-6">
                                    <Button
                                        fullWidth
                                        className="mb-3"
                                        secondary
                                        label="Back"
                                        onClick={() =>
                                            ctx.handleSetDesignQuoterStep(DESIGN_QUOTER_STEP_QUOTER)
                                        }
                                    />
                                </div>
                                <div className="col-sm-6">
                                    <Button
                                        disabled={
                                            !canContinueToThirdStep ||
                                            ctx.isAddingToCart ||
                                            ctx.isSaving
                                        }
                                        fullWidth
                                        className="mb-3"
                                        primary
                                        label="Next"
                                        onClick={() =>
                                            ctx.handleSetDesignQuoterStep(steps[stepIndex + 1])
                                        }
                                    />
                                </div>
                            </div>
                        )}
                        {stepIndex === 1 && (
                            <div className="row">
                                <div className="col-sm-6">
                                    <Button
                                        fullWidth
                                        className="mb-3"
                                        secondary
                                        label="Back"
                                        onClick={() =>
                                            ctx.handleSetDesignQuoterStep(steps[stepIndex - 1])
                                        }
                                    />
                                </div>
                                <div className="col-sm-6">
                                    <Button
                                        disabled={
                                            !canContinueToFourthStep ||
                                            ctx.isAddingToCart ||
                                            ctx.isSaving
                                        }
                                        fullWidth
                                        className="mb-3"
                                        primary
                                        label="Next"
                                        onClick={() =>
                                            ctx.handleSetDesignQuoterStep(steps[stepIndex + 1])
                                        }
                                    />
                                </div>
                            </div>
                        )}
                        {stepIndex === 2 && (
                            <div className="row">
                                <div className="col-sm-6">
                                    <Button
                                        disabled={disableAddToCart}
                                        fullWidth
                                        className="mb-3"
                                        secondary
                                        label="Back"
                                        onClick={() =>
                                            ctx.handleSetDesignQuoterStep(steps[stepIndex - 1])
                                        }
                                    />
                                </div>
                                <div className="col-sm-6">
                                    <Button
                                        disabled={
                                            disableAddToCart ||
                                            !canContinueToCheckout ||
                                            ctx.isAddingToCart ||
                                            ctx.isSaving
                                        }
                                        fullWidth
                                        className="mb-3"
                                        primary
                                        label={isOrderEdit ? 'Save Changes' : 'Add to Cart'}
                                        onClick={() => {
                                            setDisableAddToCart(true), ctx.handleAddToCart();
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="text-center">Step {stepIndex + 2} of 4</div>
                </>
            )}
            {order.status_id !== ORDER_STATUS_QUOTE_SAVED && (
                <Button
                    secondary
                    fullWidth
                    label={isOrderEdit ? 'Changes are Autosaved' : 'Save Quote'}
                    disabled={
                        errors.findIndex(({category}) => category === 'comments') > -1 ||
                        !order.substrate_id ||
                        ctx.isAddingToCart ||
                        isSavingToAccount ||
                        ctx.isSaving ||
                        isOrderEdit
                    }
                    onClick={() => setShowModal(1)}
                />
            )}

            {displayLoginModal && (
                <ModalPortal onClose={toggleDisplayLoginModal} noCard={true} maxWidth="700px">
                    <div style={{background: 'white', padding: '20px', position: 'relative'}}>
                        <ModalClose onClose={toggleDisplayLoginModal} />
                        <QuoteLoginModal
                            orderId={ctx.order.id}
                            defaultLabel={ctx.order.customer_label}
                            onLogin={handleLogin}
                            onShowCreateAccount={handleShowCreateAccount}
                        />
                    </div>
                </ModalPortal>
            )}

            {order.status_id === ORDER_STATUS_QUOTE_SAVED && (
                <p className="text-center">Quote Saved Automatically</p>
            )}

            {saveQuoteError && (
                <div className="mt-2">
                    <Alert title="">
                        There was a problem saving this quote: {saveQuoteError.message}
                    </Alert>
                </div>
            )}
            <div className="row">
                <div className="col-md-12">
                    <p>
                        Once your order is placed and we review your artwork files, additional
                        charges may apply if your file is uneditable or if any area is
                        low-resolution and needs to be recreated by our design team.
                    </p>
                    <p>
                        <em>
                            Charges may apply if additional edits are requested after receiving your
                            order.
                        </em>
                    </p>
                </div>
            </div>
            <OrderValidation order={order} errors={errors} />
            <Section enabled={ctx.canEstimateDesignDelivery(ctx.order)} className="mt-8">
                <h4>
                    Estimated Proof Delivery
                    <Popover
                        title="Estimated Proof Delivery"
                        content={
                            <p>
                                This date is when you should expect to receive your first digital
                                design proof to review. Any additional artwork revisions you may
                                need will add additional processing time to your design order.
                            </p>
                        }>
                        <IconButton secondary icon="info" />
                    </Popover>
                </h4>
                <DesignDeliveryEstimator />
            </Section>
        </>
    );
}

function OrderValidation({errors}) {
    const hasErrors = errors && errors.length > 0;

    if (!hasErrors) return null;

    return (
        <div className="mt-4 w-100">
            <QuoterValidation
                icon="warning"
                subTitle="Items Incomplete"
                steps={errors.map(e => e.message)}
            />
        </div>
    );
}
